import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Menu,
  MenuItem as MUIMenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  FormControlLabel,
  Checkbox,
  Tooltip,
  TextField,
  List,
  ListItem,
  Autocomplete,
  CircularProgress,
  InputAdornment,
  Chip,
  Popper,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloseIcon from "@mui/icons-material/Close";
import SearchBar from "../components/SearchBar";
import SelectDropdown, {
  SelectDropdownOption,
} from "../components/SelectDropdown";
import axios from "axios";
import { getFaqs, deleteFaq, fetchPrograms } from "../api/faqApi";
import ConfirmDialog from "../components/ConfirmDialog";
import EditFaqDialog from "../components/EditFaqDialog";
import CreateFaqDialog from "../components/CreateFaqDialog";
import { ProgramOption, GetFaqPayload } from "../api/types";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import debounce from "lodash/debounce";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import SearchIcon from "@mui/icons-material/Search";

interface FaqData {
  _id?: string;
  question: string;
  answer: string;
  is_global: boolean;
  programs?: { code: string; name: string; pok?: string[] }[];
  status?: number;
  created_at?: string;
}

interface FaqSuggestion {
  _id: string;
  question: string;
  answer: string;
  programs?: { code: string; name: string }[];
  is_global: boolean;
}

interface ApiResponse {
  output: FaqSuggestion[];
  metadata: {
    run_id: string;
    feedback_tokens: any[];
  };
}

const dummyDropdownOptions: SelectDropdownOption[] = [
  { label: "PPK 1", value: "ppk1" },
  { label: "PPK 2", value: "ppk2" },
  { label: "PPK 3", value: "ppk3" },
];

// Dummy API simulation
const DUMMY_FAQ_SUGGESTIONS = [
  "How to enroll?",
  "How to change track?",
  "How to reset password?",
  "How to contact support?",
  "How to de-enroll?",
  "How to view approval status?",
  "Deferrals",
];

const DUMMY_FAQ_LIST: Record<string, { question: string; answer: string }[]> = {
  Deferrals: [
    {
      question: "What are the conditions and criteria for applying deferrals?",
      answer:
        "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.",
    },
    {
      question:
        "Is Deferral possible for any batches or is it specific for for only certain batches?",
      answer: "Answer for batch-specific deferral.",
    },
    {
      question: "How many deferrals are applicable for one course?",
      answer: "Answer for number of deferrals.",
    },
    {
      question: "Can I cancel my deferral at anytime before the batch starts?",
      answer: "Answer for canceling deferral.",
    },
    {
      question:
        "Is Deferral possible for any batches or is it specific for for only certain batches?",
      answer: "Answer for batch-specific deferral (duplicate).",
    },
    {
      question: "How many deferrals are applicable for one course?",
      answer: "Answer for number of deferrals (duplicate).",
    },
    {
      question: "What are the conditions and criteria for applying deferrals?",
      answer: "Answer for conditions and criteria (duplicate).",
    },
  ],
};

// Dummy API functions
const fetchFaqSuggestionsAPI = (query: string): Promise<string[]> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      if (!query) return resolve([]);
      resolve(
        DUMMY_FAQ_SUGGESTIONS.filter((faq) =>
          faq.toLowerCase().includes(query.toLowerCase())
        )
      );
    }, 400);
  });
};

const fetchFaqListAPI = (
  keyword: string
): Promise<{ question: string; answer: string }[]> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(DUMMY_FAQ_LIST[keyword] || DUMMY_FAQ_LIST["Deferrals"]);
    }, 400);
  });
};

// Helper function to truncate text
const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) {
    return text;
  }
  return text.substring(0, maxLength) + "...";
};

const FAQDashboard: React.FC = () => {
  const [searchValue, setSearchValue] = useState("");
  const [suggestions, setSuggestions] = useState<FaqSuggestion[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] =
    useState<FaqSuggestion | null>(null);
  const [faqList, setFaqList] = useState<FaqData[]>([]);
  const [faqLoading, setFaqLoading] = useState(false);
  const [expanded, setExpanded] = useState<number | false>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuIndex, setMenuIndex] = useState<number | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [editQuestion, setEditQuestion] = useState("");
  const [editAnswer, setEditAnswer] = useState("");
  const [globalVisibility, setGlobalVisibility] = useState(false);
  const [selectedPrograms, setSelectedPrograms] = useState<ProgramOption[]>([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "info" | "warning",
  });
  const [programSearchValue, setProgramSearchValue] = useState("");
  const [programOptions, setProgramOptions] = useState<ProgramOption[]>([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [faqToDelete, setFaqToDelete] = useState<FaqData | null>(null);
  const [faqToEdit, setFaqToEdit] = useState<FaqData | null>(null);

  // New state for the multi-select program (PPK) dropdown
  const [ppkProgramOptions, setPpkProgramOptions] = useState<ProgramOption[]>(
    []
  );
  const [selectedPpkPrograms, setSelectedPpkPrograms] = useState<
    ProgramOption[]
  >([]);
  const [ppkSearchValue, setPpkSearchValue] = useState("");
  const [isPpkDropdownOpen, setIsPpkDropdownOpen] = useState(false);
  const [loadingPpkPrograms, setLoadingPpkPrograms] = useState(false);

  // Debounced function for fetching programs for the PPK Autocomplete
  const debouncedPpkProgramSearch = useCallback(
    debounce(async (query: string) => {
      setLoadingPpkPrograms(true);
      try {
        const programs = await fetchPrograms(query); // Assuming fetchPrograms takes (query, codes, skip) - we don't need skip here
        setPpkProgramOptions(programs);
      } catch (error) {
        console.error("Error fetching PPK programs:", error);
        setPpkProgramOptions([]);
      } finally {
        setLoadingPpkPrograms(false);
      }
    }, 300),
    [] // fetchPrograms is a stable import
  );

  useEffect(() => {
    // Fetch programs when dropdown is opened or search value changes
    // Only fetch if dropdown is open to avoid unnecessary calls
    if (isPpkDropdownOpen) {
      debouncedPpkProgramSearch(ppkSearchValue);
    }
    return () => {
      debouncedPpkProgramSearch.cancel(); // Clean up debounce
    };
  }, [ppkSearchValue, isPpkDropdownOpen, debouncedPpkProgramSearch]);

  // Unified function to fetch FAQs based on payload
  const executeFaqSearch = useCallback(async (payload: GetFaqPayload) => {
    console.log(
      "[FAQDashboard] executeFaqSearch called with payload:",
      payload
    ); // Ensure this log is present
    setFaqLoading(true);
    setFaqList([]); // Clear previous results
    setSelectedSuggestion(null); // Clear selected suggestion indication
    setExpanded(false); // Collapse accordion
    try {
      console.log("Fetching FAQs with payload:", payload); // Debug log
      const response = await getFaqs(payload);
      // Assuming response.output contains the array of FAQs
      const fetchedFaqs = response?.output?.data || [];
      console.log("Received FAQs:", fetchedFaqs);
      setFaqList(fetchedFaqs);
      if (fetchedFaqs.length === 1) {
        setExpanded(0); // Expand if only one result
      }
    } catch (error) {
      console.error("Error fetching FAQs:", error);
      setSnackbar({
        open: true,
        message:
          error instanceof Error ? error.message : "Failed to fetch FAQs",
        severity: "error",
      });
      setFaqList([]); // Ensure list is empty on error
    } finally {
      setFaqLoading(false);
    }
  }, []);

  // Debounced function for fetching *suggestions* only
  const debouncedSuggestionSearch = useCallback(
    debounce(async (query: string) => {
      if (!query) {
        setSuggestions([]);
        setLoadingSuggestions(false);
        return;
      }
      setLoadingSuggestions(true);
      try {
        // Using getFaqs for suggestions as well, might need a dedicated endpoint later
        const response = await getFaqs({ question: query });
        setSuggestions(response?.output?.data || []);
      } catch (error) {
        console.error("Error fetching suggestions:", error);
        setSuggestions([]);
      } finally {
        setLoadingSuggestions(false);
      }
    }, 500),
    [] // Assuming getFaqs doesn't change
  );

  // Update suggestion fetching effect
  useEffect(() => {
    if (searchValue) {
      debouncedSuggestionSearch(searchValue);
    } else {
      setSuggestions([]);
      setLoadingSuggestions(false);
    }
    return () => {
      debouncedSuggestionSearch.cancel();
    };
  }, [searchValue, debouncedSuggestionSearch]);

  // Handle search input change
  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setSelectedSuggestion(null); // Clear suggestion selection when typing
    if (!value.trim()) {
      setFaqList([]); // Clear FAQ list when search is empty
      setExpanded(false); // Collapse accordion
    }
  };

  // Handler for Enter key press in main search bar
  const handleEnterPress = () => {
    if (searchValue.trim()) {
      // Clear PPK filter if active
      if (selectedPpkPrograms.length > 0) {
        setSelectedPpkPrograms([]);
        setPpkSearchValue("");
      }
      executeFaqSearch({ question: searchValue.trim() });
    }
  };

  // Handler for suggestion selection
  const handleSuggestionSelect = (suggestion: FaqSuggestion) => {
    // Clear PPK filter if active
    if (selectedPpkPrograms.length > 0) {
      setSelectedPpkPrograms([]);
      setPpkSearchValue("");
    }
    setSearchValue(suggestion.question); // Update search bar text
    setSelectedSuggestion(suggestion); // Keep track of selected suggestion if needed
    setSuggestions([]); // Clear suggestions list
    if (suggestion._id) {
      executeFaqSearch({ faq_id: suggestion._id }); // Fetch the specific FAQ by ID
    } else {
      // Fallback if ID is somehow missing
      executeFaqSearch({ question: suggestion.question });
    }
  };

  const handleAccordionChange =
    (panel: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    idx: number
  ) => {
    setAnchorEl(event.currentTarget);
    setMenuIndex(idx);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuIndex(null);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    setDeleteIndex(menuIndex);
    handleMenuClose();
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDeleteIndex(null);
  };

  const handleDeleteConfirm = async () => {
    if (deleteIndex !== null) {
      try {
        const faqToDelete = faqList[deleteIndex];
        if (!faqToDelete._id) {
          throw new Error("FAQ ID is missing");
        }
        console.log("Deleting FAQ with ID:", faqToDelete._id);
        await deleteFaq(faqToDelete._id);
        setFaqList((prev) => prev.filter((_, idx) => idx !== deleteIndex));
        setSnackbar({
          open: true,
          message: "FAQ deleted successfully",
          severity: "success",
        });
      } catch (error) {
        console.error("Error in handleDeleteConfirm:", error);
        setSnackbar({
          open: true,
          message: "Failed to delete FAQ. Please try again.",
          severity: "error",
        });
      }
    }
    setDeleteDialogOpen(false);
    setDeleteIndex(null);
  };

  const handleEditClick = () => {
    if (menuIndex !== null && faqList[menuIndex]) {
      const faq = faqList[menuIndex];
      setFaqToEdit(faq); // Store the whole FAQ object
      setEditDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setFaqToEdit(null); // Clear the FAQ object on close
  };

  const handleEditSave = () => {
    if (editIndex !== null) {
      setFaqList((prev) =>
        prev.map((faq, idx) =>
          idx === editIndex
            ? { ...faq, question: editQuestion, answer: editAnswer }
            : faq
        )
      );
    }
    setEditDialogOpen(false);
    setEditIndex(null);
    setSnackbar({
      open: true,
      message: "FAQ updated successfully",
      severity: "success",
    });
  };

  const handleProgramSearch = (query: string) => {
    setProgramSearchValue(query);
    debouncedProgramSearch(query);
  };

  const handleCreateSave = (data: {
    question: string;
    answer: string;
    globalVisibility: boolean;
    selectedPrograms: ProgramOption[];
  }) => {
    setFaqList((prev) => [
      ...prev,
      {
        question: data.question,
        answer: data.answer,
        is_global: data.globalVisibility,
        programs: data.selectedPrograms.map((p) => ({
          code: p.value,
          name: p.label,
        })),
      },
    ]);
    setSnackbar({
      open: true,
      message: "FAQ created successfully",
      severity: "success",
    });
    setSearchValue("");
    setSelectedSuggestion(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleClearSearch = () => {
    setSearchValue("");
    setSelectedSuggestion(null);
    setFaqList([]);
  };

  const handleDeleteFromDialog = async (faqId: string) => {
    try {
      await deleteFaq(faqId);
      setFaqList((prev) => prev.filter((faq) => faq._id !== faqId));
      setSnackbar({
        open: true,
        message: "FAQ deleted successfully",
        severity: "success",
      });
    } catch (error) {
      console.error("Failed to delete FAQ from dialog:", error);
      setSnackbar({
        open: true,
        message: "Failed to delete FAQ",
        severity: "error",
      });
    }
  };

  // Function to fetch program options (might need to be moved or adjusted based on CreateFaqDialog changes)
  const fetchProgramOptions = async (query: string) => {
    // This logic might no longer be needed here if CreateFaqDialog handles its own program fetching
    // If still needed for filtering *outside* the dialog, use fetchPrograms from faqApi
    /* 
    try {
      const programs = await fetchPrograms(query); // Use fetchPrograms from faqApi
      setProgramOptions(programs);
    } catch (error) {
      console.error("Error fetching program options:", error);
      setProgramOptions([]); // Set empty on error
    }
    */
  };

  // Debounced version for search input
  const debouncedProgramSearch = debounce(fetchProgramOptions, 300);

  // Add handlers for Create Dialog
  const handleCreate = () => {
    setIsCreateDialogOpen(true);
  };

  const handleCreateDialogClose = () => {
    setIsCreateDialogOpen(false);
  };

  const handleCreateDialogSave = (data: {
    faqs: { question: string; answer: string }[];
    globalVisibility: boolean;
    selectedPrograms: ProgramOption[];
  }) => {
    console.log("Saving new FAQs:", data);
    const newFaqs = data.faqs.map((faq) => ({
      question: faq.question,
      answer: faq.answer,
      is_global: data.globalVisibility,
      programs: data.selectedPrograms.map((p) => ({
        code: p.value,
        name: p.label,
      })),
    }));
    setFaqList((prev) => [...prev, ...newFaqs]);
    setSnackbar({
      open: true,
      message: "FAQs created successfully",
      severity: "success",
    });
    handleCreateDialogClose();
  };

  const confirmDelete = async () => {
    if (faqToDelete && faqToDelete._id) {
      const faqIdToDelete = faqToDelete._id;
      try {
        await deleteFaq(faqIdToDelete);
        setFaqList((prev) => prev.filter((faq) => faq._id !== faqIdToDelete));
        setSnackbar({
          open: true,
          message: "FAQ deleted successfully",
          severity: "success",
        });
      } catch (error) {
        console.error("Failed to delete FAQ:", error);
        setSnackbar({
          open: true,
          message: "Failed to delete FAQ",
          severity: "error",
        });
      }
      setDeleteIndex(null);
      setFaqToDelete(null);
      setIsConfirmDialogOpen(false);
    }
  };

  // Update Edit Success handler if needed (e.g., refetch list)
  const handleEditSuccess = () => {
    setSnackbar({
      open: true,
      message: "FAQ updated successfully!", // Message moved from dialog
      severity: "success",
    });
    // Refetch the list or update locally if necessary
    // Prioritize selected PPK programs for refetching
    if (selectedPpkPrograms.length > 0) {
      const allProgramCodes = selectedPpkPrograms.reduce((acc, program) => {
        let codesToProcess: string[] = [];
        if (program.code) {
          if (Array.isArray(program.code)) {
            codesToProcess = program.code;
          } else if (typeof program.code === "string") {
            codesToProcess = [program.code];
          }
        }
        if (codesToProcess.length > 0) {
          codesToProcess.forEach((codeValue) => {
            if (
              codeValue &&
              typeof codeValue === "string" &&
              !acc.includes(codeValue)
            ) {
              acc.push(codeValue);
            }
          });
        }
        return acc;
      }, [] as string[]);
      if (allProgramCodes.length > 0) {
        executeFaqSearch({ program_code: allProgramCodes });
      } else {
        // If selectedPpkPrograms is populated but results in no codes (edge case),
        // fall back to other search criteria or clear list.
        // For now, let's consider if a general question search should apply.
        // If searchValue exists, it might be better to use that.
        if (searchValue.trim()) {
          executeFaqSearch({ question: searchValue.trim() });
        } else {
          setFaqList([]); // Or fetch global FAQs if that's desired behavior
          setExpanded(false);
        }
      }
    } else if (searchValue.trim()) {
      executeFaqSearch({ question: searchValue.trim() });
    } else if (programSearchValue.trim()) {
      // This was 'programSearchValue' which is for the input field, not selected items
      // This case might be less common if PPK multi-select is the primary program filter
      // Consider if this is still needed or if global FAQs should be fetched.
      // For now, keeping it, but it might conflict if ppkSearchValue was used to find ppkProgramOptions
      // but then cleared without selecting.
      // A safer bet if nothing else is selected might be to fetch nothing or global.
      executeFaqSearch({ question: programSearchValue.trim() });
    } else {
      // If no filters are active, perhaps clear the list or fetch all global FAQs
      // For now, clearing the list as per existing pattern in Autocomplete onChange
      setFaqList([]);
      setExpanded(false);
    }
    // Optionally, could update the specific item in faqList state directly
  };

  // Update Delete Success handler if needed (e.g., refetch list)
  const handleDeleteSuccess = () => {
    setSnackbar({
      open: true,
      message: "FAQ deleted successfully!", // Message moved from dialog
      severity: "success",
    });
    // Refetch the list or update locally
    if (searchValue.trim()) {
      executeFaqSearch({ question: searchValue.trim() });
    } else if (programSearchValue) {
      executeFaqSearch({ question: programSearchValue });
    }
    // Optionally, remove the item from faqList state directly
  };

  return (
    <Box
      sx={{
        background: "#fff",
        borderRadius: 3,
        p: 4,
        minHeight: 520,
        boxShadow: "none",
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      <Box sx={{ display: "flex", gap: 3, mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <SearchBar
            value={searchValue}
            onChange={handleSearchChange}
            onEnterPress={handleEnterPress}
            loading={loadingSuggestions}
            suggestions={suggestions || []}
            placeholder="Search FAQs using keywords"
            onSelectSuggestion={handleSuggestionSelect}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: "48px",
                bgcolor: "#f9fbfc",
                borderRadius: "8px",
                border: "1px solid #d3e1e6",
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                "&:hover": {
                  borderColor: "#d3e1e6",
                },
                "&.Mui-focused": {
                  borderColor: "#000",
                },
              },
              "& .MuiInputBase-input": {
                fontSize: "14px",
                height: "48px",
                padding: "0 14px",
              },
            }}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Autocomplete
            multiple
            id="ppk-program-select"
            open={isPpkDropdownOpen}
            onOpen={() => setIsPpkDropdownOpen(true)}
            onClose={() => setIsPpkDropdownOpen(false)}
            options={ppkProgramOptions || []}
            value={selectedPpkPrograms || []}
            loading={loadingPpkPrograms}
            inputValue={ppkSearchValue}
            onInputChange={(event, newValue) => {
              setPpkSearchValue(newValue);
            }}
            onChange={(event, newValue: ProgramOption[]) => {
              setSelectedPpkPrograms(newValue || []);

              // Clear question search filter if active
              if (searchValue.trim()) {
                setSearchValue("");
                setSelectedSuggestion(null);
              }

              const allProgramCodes = (newValue || []).reduce(
                (acc, program) => {
                  let codesToProcess: string[] = [];
                  if (program.code) {
                    if (Array.isArray(program.code)) {
                      codesToProcess = program.code;
                    } else if (typeof program.code === "string") {
                      codesToProcess = [program.code];
                    }
                  }
                  if (codesToProcess.length > 0) {
                    codesToProcess.forEach((codeValue) => {
                      if (
                        codeValue &&
                        typeof codeValue === "string" &&
                        !acc.includes(codeValue)
                      ) {
                        acc.push(codeValue);
                      }
                    });
                  }
                  return acc;
                },
                [] as string[]
              );

              if (allProgramCodes.length > 0) {
                executeFaqSearch({ program_code: allProgramCodes });
              } else {
                setFaqList([]);
                setExpanded(false);
              }
            }}
            getOptionLabel={(option) => option?.label || ""}
            isOptionEqualToValue={(option, value) =>
              option?.value === value?.value
            }
            disableCloseOnSelect
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={
                  selectedPpkPrograms?.length > 0
                    ? `${selectedPpkPrograms[0]?.label || ""}${
                        selectedPpkPrograms.length > 1
                          ? ` +${selectedPpkPrograms.length - 1}`
                          : ""
                      }`
                    : "Search & Select Programs"
                }
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: null,
                  endAdornment: params.InputProps.endAdornment,
                  sx: {
                    background: "#f9fbfc",
                    borderRadius: "8px",
                    border: "1px solid #d3e1e6",
                    boxSizing: "border-box",
                    height: "48px",
                    padding: 0, // no internal padding here
                  },
                }}
                sx={{
                  "& .MuiInputBase-input": {
                    fontSize: "16px",
                    fontWeight: 400,
                    padding: "12px 14px", // balanced vertical & horizontal padding
                    height: "48px", // match overall input height
                    position: "relative",
                    top: "-17px", // helps with vertical centering
                  },
                  "& input::placeholder": {
                    color: "#bdbdbd",
                    opacity: 1,
                    fontSize: "16px",
                    fontWeight: 400,
                  },
                }}
              />
            )}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Box
                  sx={{ display: "flex", alignItems: "center", width: "100%" }}
                >
                  <Checkbox
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    checked={selected}
                    sx={{ mr: 1 }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2">
                      {option?.label || ""}
                      <span
                        style={{
                          fontSize: "14px",
                          color: "#777",
                          marginLeft: "4px",
                        }}
                      >
                        ({option?.code})
                      </span>
                    </Typography>

                    {option?.pok && option.pok.length > 0 && (
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", display: "block" }}
                      >
                        POKs:{" "}
                        {Array.isArray(option.pok)
                          ? option.pok.join(", ")
                          : option.pok}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </li>
            )}
          />
        </Box>
      </Box>
      <Box mt={2}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
          {faqLoading && "Loading FAQs..."}
        </Typography>
        <Box
          sx={{
            maxHeight: 530,
            overflowY: "auto",
            "&::-webkit-scrollbar": {
              width: "4px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#888",
              borderRadius: "4px",
              "&:hover": {
                background: "#555",
              },
            },
          }}
        >
          {faqLoading ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              Loading...
            </Typography>
          ) : faqList.length > 0 ? (
            faqList.map((faq, idx) => (
              <Accordion
                key={faq._id || idx}
                expanded={expanded === idx}
                onChange={handleAccordionChange(idx)}
                sx={{
                  boxShadow: "none",
                  border: "1px solid #eee",
                  borderRadius: 0,
                  margin: "0 !important",
                  "&.Mui-expanded": {
                    margin: "0 !important",
                  },
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`panel${idx}-content`}
                  id={`panel${idx}-header`}
                  sx={{
                    background: expanded === idx ? "#fafafa" : "#fff",
                    fontWeight: 600,
                  }}
                >
                  <Box sx={{ flex: 1 }}>{faq.question}</Box>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMenuOpen(e, idx);
                    }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </AccordionSummary>
                <AccordionDetails sx={{ background: "#fafafa" }}>
                  <Typography variant="body2" color="text.secondary">
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))
          ) : (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 320,
                gap: "10px",
              }}
            >
              <img
                src="/svgs/faqData.svg"
                alt="FaqData"
                style={{ width: 18, height: 18 }}
              />
              <Typography fontSize={14} color="#929292">
                {searchValue || programSearchValue
                  ? "No FAQs found matching your criteria."
                  : "Search by question or select a POK to view FAQs."}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            boxShadow: "0px 4px 16px rgba(0,0,0,0.12)",
            borderRadius: 2,
            mt: 1,
          },
        }}
      >
        <MUIMenuItem onClick={handleEditClick}>Edit</MUIMenuItem>
        <MUIMenuItem onClick={handleDeleteClick}>Delete</MUIMenuItem>
      </Menu>
      <CreateFaqDialog
        open={isCreateDialogOpen}
        onClose={handleCreateDialogClose}
        onSave={handleCreateDialogSave}
      />
      {editDialogOpen && faqToEdit && faqToEdit._id && (
        <EditFaqDialog
          open={editDialogOpen}
          id={faqToEdit._id} // Pass the guaranteed string ID
          initialQuestion={faqToEdit.question}
          initialAnswer={faqToEdit.answer}
          initialGlobalVisibility={faqToEdit.is_global}
          // Map programs from FaqData to ProgramOption for the dialog
          initialSelectedPrograms={(faqToEdit.programs || []).map((p) => ({
            label: p.name || `Unknown Program (${p.code})`, // Prioritize name, provide better fallback
            value: p.code,
            code: p.code,
            pok: p.pok, // Add the pok field here
          }))}
          onClose={handleEditClose}
          onSuccess={handleEditSuccess} // Use updated handler
          onDeleteSuccess={handleDeleteSuccess} // Use updated handler
        />
      )}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete FAQ"
        description="Are you sure you want to delete this FAQ? This action cannot be undone."
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FAQDashboard;
